import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { blockSpecialNumberKeys, blockSpecialNumberPaste, blockSpecialNumberDrop } from '../arv/fieldLimits'
import { PLACEHOLDER, formatMoneyInput } from './format'
import { formatScenarioDate, isBlankDate, toIsoDate } from './scenarioDates'
import { sameDate, sameFigure, useFieldHistory } from './fieldHistory'

// Spec section 1.3 -- the bordered/borderless split is "the rule most likely to be applied to only
// half the rows", so both boxes are built here as one pair rather than two independently-styled
// components that could drift apart.

const BOX_BASE = 'h-[32px] rounded-[4px] flex items-center px-[8px]'

// Letter 99, 5.4: an editable box's hover and focus are the same 1px #4CBC18 border, replacing the old
// 2px focus ring. An invalid date keeps its red border in every state.
const EDITABLE_BORDER = 'border border-brand-fieldBdr hover:border-brand-green focus-within:border-brand-green'
// Tailwind emits `hover:` after `focus-within:`, so a plain hover would turn a focused box green.
const ERROR_BORDER = 'border border-brand-fieldBdr hover:[&:not(:focus-within)]:border-brand-green focus-within:border-brand-statusSale'

// Timeline's "mo" (letter 122 item 3: 14/400 #8A9A85, about 4 after the figure).
export function ValueUnit({ children }) {
  return <span className="shrink-0 ml-[4px] text-[14px] font-normal text-brand-placeholder">{children}</span>
}

// Every placeholder goes through this one function -- plain weight/colour even inside a bold total
// row, since an absent value must not inherit the emphasis of a present one (the same rule
// features/arv/EstimateMetrics.jsx documents for its own dash).
function Placeholder() {
  return <span className="text-[14px] font-normal text-brand-placeholder">{PLACEHOLDER}</span>
}

// Read-only, right-aligned (spec 1.4). `empty` draws neither value nor placeholder. `topDivider`: README.md ("the total divider is
// the box's own border, not a sibling").
// `valueColorClass`: Flip Summary's Net Profit total row (spec 6) is the one value in this codebase
// drawn in a colour other than brand.dark or the placeholder grey (#1F7A2E, "the same literal
// DealScoreBlock.jsx's 80-100 band already uses, not a Tailwind token"; brand-statusSale when it
// reads Net Loss). Defaulted so every existing call site is unaffected -- see README.md ("Total
// Expenses / Net Profit total-row treatment").
// `width` is a minimum: a longer figure ("$1,746,600") widens the box instead of spilling into its
// padding, and the row's right alignment keeps its right edge where it was (letter 111 item 6).
export function DerivedValueBox({ value, width = 88, hug = false, bold = false, empty = false, topDivider = false, valueColorClass = 'text-brand-dark', ariaLabel }) {
  const style = hug ? undefined : { minWidth: width }
  return (
    <div
      aria-label={ariaLabel}
      data-testid={ariaLabel}
      // `pb-[2px]` matches the 2px `border-t-2` eats from this border-box element's content height --
      // without it, items-center centers within a top-only-shrunk band and the value sits 1px low.
      // EditableValueBox's own 1px border (all four sides) never needed this: it shrinks symmetrically.
      className={`${BOX_BASE} justify-end shrink-0 bg-white ${hug ? 'px-[8px]' : ''} ${topDivider ? 'border-t-2 border-brand-fieldBdr pb-[2px]' : ''}`}
      style={style}
    >
      {empty ? null : value != null ? (
        <span className={`whitespace-nowrap text-[14px] ${bold ? 'font-bold' : 'font-normal'} ${valueColorClass}`}>{value}</span>
      ) : (
        <Placeholder />
      )}
    </div>
  )
}

// setSelectionRange, not select(): iOS Safari honours it more reliably.
function selectAll(el) {
  el?.setSelectionRange(0, el.value.length)
}

// Editable: bordered, user-writable, local state only -- see pages/Flip.jsx for why nothing here
// calls a save endpoint. `suffix` is a static character (e.g. "%") drawn beside the input rather than
// baked into its value, so the field never has to reformat text the user is mid-way through typing.
// `focus-within` marks the whole box (not `focus-visible` on the input itself, which every button
// in this feature uses instead) -- see EDITABLE_BORDER. A disabled box takes no hover.
// `draft`: the parent stores a parsed number, so echoing it back mid-typing would eat '12.' or '1,2'.
// The box shows what was typed until a real blur, then the stored value again -- README.md ("scenario
// fields hold numbers, not typed text").
// `placeholder`: shown only while the stored value is blank -- Property Tax Proration's computed
// fallback, README.md ("Selling Costs rows"). Grey, unless `placeholderDark`: the computed figure is
// drawn as a value, in the typed colour (letter 145 item 1), while the input itself stays blank.
// `placeholderDescription`: says what the placeholder figure is, for screen readers (aria-describedby
// to visually hidden text, figure included -- a describedby replaces the placeholder as the input's
// description). Present only while the placeholder is actually showing.
// `validate(text)`: the caller's warning message for a figure, or null (spec 4.4, the payment field).
// Focused, it judges what is typed now and the box turns red; unfocused, the stored value, with no red
// border and the callout on hover only (Cong, letter 111, R2). README.md ("Payment Range").
// `money` / `cents`: unfocused, the stored figure reads "$285,000" ("$2,354.63" with `cents`); focused,
// the raw number to edit. Display only. A money box's `width` is a minimum: it grows to fit the
// figure, sized by an invisible copy of the text, since an input does not size to its value --
// README.md ("Money boxes show $xxx,xxx").
// `unit`: a grey word after the figure ("mo", Timeline, letter 122 item 3), 4 after it -- unlike `suffix`, not dark.
// No `placeholder` prop at all: a blank box reads a grey "$0" / "0" (+ a grey `suffix`, so "0%") --
// letter 122 item 4. A computed-fallback caller passes `null` to keep its own blank. README.md ("Blank reads $0 / 0%").
// Ctrl/Cmd+Z and redo run on the box's own history (letter 122 item 2) -- README.md ("Undo and redo in a field").
// `max`: the upper bound the parent holds this field to (Timeline 600), so a clamped figure keeps its history.
export function EditableValueBox({ value, onChange, width = 88, suffix, unit, disabled = false, placeholder, placeholderDark = false, placeholderDescription, validate, money = false, cents = false, max, ariaLabel }) {
  const [draft, setDraft] = useState(null)
  const [focused, setFocused] = useState(false)
  const inputRef = useRef(null)
  const selectOnFocus = useRef(false)
  const pointerFocusing = useRef(false)
  const descriptionId = useId()
  const errorId = useId()
  const raw = draft ?? value ?? ''
  const shown = money && !focused && draft === null ? formatMoneyInput(raw, { cents }) : raw
  const shownPlaceholder = placeholder === undefined ? (money ? formatMoneyInput('0', { cents }) : '0') : placeholder
  const placeholderShowing = raw === '' && Boolean(shownPlaceholder)
  const history = useFieldHistory(
    inputRef,
    (text) => {
      pointerFocusing.current = false
      setDraft(text)
      onChange(text)
    },
    (present, shown) => sameFigure(present, shown, max)
  )
  // Focusing (click or Tab) selects the whole figure, after the "$0" -> "0" swap has rendered, so
  // typing replaces it rather than landing beside it -- README.md ("Focus selects the whole figure").
  useLayoutEffect(() => {
    if (focused && selectOnFocus.current) {
      selectOnFocus.current = false
      selectAll(inputRef.current)
    }
  }, [focused])
  // Only ever set, never cleared, here: a touch's late mousedown must not undo its own pointerdown.
  // A press released outside the box never reaches its onMouseUp, so any mouseup clears the mark
  // (document bubbling runs after React's own handler, which has already used it).
  const clearOnAnyMouseUp = useRef(null)
  const markPointerFocusing = (e) => {
    if (document.activeElement === e.target || pointerFocusing.current) return
    pointerFocusing.current = true
    const clear = () => {
      pointerFocusing.current = false
      clearOnAnyMouseUp.current = null
    }
    clearOnAnyMouseUp.current = clear
    document.addEventListener('mouseup', clear, { once: true })
  }
  useEffect(() => () => {
    if (clearOnAnyMouseUp.current) document.removeEventListener('mouseup', clearOnAnyMouseUp.current)
  }, [])
  const describePlaceholder = Boolean(placeholder && placeholderDescription && raw === '')
  const error = !disabled && validate ? validate(focused ? raw : value ?? '') : null
  const showError = Boolean(error)
  const describedBy = [describePlaceholder ? descriptionId : null, showError ? errorId : null].filter(Boolean).join(' ')
  const borderCls = disabled ? 'border border-brand-fieldBdr opacity-60' : showError && focused ? ERROR_BORDER : EDITABLE_BORDER
  // Every text the box can show, so focusing never narrows it; the box follows the typed value (min = design width).
  const sizerTexts = money ? [...new Set([shown, formatMoneyInput(value ?? '', { cents }), shownPlaceholder ?? ''])].filter(Boolean) : []
  return (
    <div
      className={`group/value relative ${BOX_BASE} justify-end bg-white ${money ? 'shrink-0' : ''} ${borderCls}`}
      style={money ? { minWidth: width } : { width }}
    >
      {money ? (
        // In flow but invisible: the widest text sets the box width; the input sits over it. 1px for the
        // caret. Drawn as ::before content, so the copies are not text nodes a text query would find twice.
        <span aria-hidden="true" data-testid="money-sizer" className="invisible grid flex-1 pr-px text-[14px] font-normal whitespace-pre">
          {sizerTexts.map((t) => (
            <span key={t} data-text={t} className="col-start-1 row-start-1 before:content-[attr(data-text)]" />
          ))}
        </span>
      ) : null}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        placeholder={shownPlaceholder ?? undefined}
        aria-describedby={describedBy || undefined}
        aria-invalid={showError || undefined}
        value={shown}
        disabled={disabled}
        onChange={(e) => {
          pointerFocusing.current = false
          history.onEdit(e, raw)
          setDraft(e.target.value)
          onChange(e.target.value)
        }}
        // Not on a return from a window switch (`focused` never dropped): the user is mid-edit.
        onFocus={() => {
          if (!focused) selectOnFocus.current = true
          history.onFocus(raw)
          setFocused(true)
        }}
        // The mouseup ending a focusing click or tap would collapse the selection to a caret. On touch
        // the synthetic mousedown can come after focus, so pointerdown marks the interaction too.
        onPointerDown={markPointerFocusing}
        onMouseDown={markPointerFocusing}
        onMouseUp={(e) => {
          if (!pointerFocusing.current) return
          pointerFocusing.current = false
          e.preventDefault()
          selectAll(e.target)
        }}
        // A window/tab switch also fires blur but leaves this input as activeElement; ending the
        // draft then would turn '12.' into '12' and resuming typing would give '125'.
        onBlur={(e) => {
          if (document.activeElement !== e.target) {
            pointerFocusing.current = false
            setDraft(null)
            setFocused(false)
          }
        }}
        onKeyDown={(e) => {
          if (!history.onKeyDown(e)) blockSpecialNumberKeys(e)
        }}
        onPaste={blockSpecialNumberPaste}
        onDrop={blockSpecialNumberDrop}
        className={`${money ? 'absolute top-0 left-[8px] h-full w-[calc(100%-16px)]' : 'min-w-0 flex-1'} bg-transparent outline-none text-right text-[14px] font-normal text-brand-dark ${placeholderDark ? 'placeholder:text-brand-dark' : 'placeholder:text-brand-placeholder'}`}
      />
      {suffix ? <span className={`shrink-0 text-[14px] font-normal ${placeholderShowing && !placeholderDark ? 'text-brand-placeholder' : 'text-brand-dark'}`}>{suffix}</span> : null}
      {unit ? <ValueUnit>{unit}</ValueUnit> : null}
      {describePlaceholder ? (
        <span id={descriptionId} className="sr-only">
          {`${placeholderDescription}: ${placeholder}`}
        </span>
      ) : null}
      {showError ? <ErrorCallout id={errorId} message={error} /> : null}
    </div>
  )
}

// Spec 4.4 `tooltip`: min 182 wide (grows for longer figures), 14 inset, 13/16 #17270D (16: letter
// 111, R2), white, rx 8, shadow 0 0 10px rgba(0,0,0,0.1); a 14x10 pointer, unshadowed, apex on the box's outer top edge
// (`bottom` counts from the padding box, 1px inside the border: 10 + 1), centred. Hidden, not unmounted: it stays the input's description.
// A `\n` in the message is a drawn line break; the lines are joined by a space for screen readers.
function ErrorCallout({ id, message }) {
  return (
    <div
      id={id}
      role="tooltip"
      className="hidden group-hover/value:block group-focus-within/value:block absolute z-30 left-1/2 -translate-x-1/2 bottom-[calc(100%+11px)] min-w-[182px] w-max rounded-[8px] bg-white p-[14px] text-left text-[13px] leading-[16px] font-normal text-brand-dark whitespace-nowrap"
      style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}
    >
      {message.split('\n').map((line, i) => (
        <Fragment key={i}>
          {i > 0 ? ' ' : null}
          <span className="block">{line}</span>
        </Fragment>
      ))}
      <svg aria-hidden="true" width="14" height="10" viewBox="0 0 14 10" className="absolute top-full left-1/2 -translate-x-1/2">
        <path d="M7 10L0 0H14Z" fill="#FFFFFF" />
      </svg>
    </div>
  )
}

// Purchase Date / Timeline. Hands up ISO 'YYYY-MM-DD' or null (cleared), never typed text, and only
// on blur or Enter: a half-typed '10/3/20' is itself a valid date (2020) and must never reach the
// compute. An invalid draft on commit stays on screen, flagged, and is not handed up, so the value
// from focus time stays in the scenario. README.md ("Purchase Date is an ISO date").
// A stored value changing from outside (load, template import) drops the draft and its error, so an
// old invalid draft cannot resurface.
export const DATE_PLACEHOLDER = 'MM/DD/YY'

// `placeholder`: the grey text while blank -- MM/DD/YY by default. `placeholderDark`: drawn in the typed
// colour instead -- Purchase Date's created + 14 on Flip Summary (letter 145). Only shown, never committed.
export function DateValueBox({ value, onChange, width = 88, ariaLabel, placeholder = DATE_PLACEHOLDER, placeholderDark = false }) {
  const [draft, setDraft] = useState(null)
  const [showError, setShowError] = useState(false)
  const [seenValue, setSeenValue] = useState(value)
  const errorId = useId()
  const inputRef = useRef(null)
  const history = useFieldHistory(inputRef, (text) => setDraft(text), sameDate)
  if (seenValue !== value) {
    setSeenValue(value)
    setDraft(null)
    setShowError(false)
  }
  const shown = draft ?? formatScenarioDate(value)
  const invalid =
    draft !== null
      ? showError && !isBlankDate(draft) && toIsoDate(draft) === null
      : !isBlankDate(value) && toIsoDate(String(value)) === null

  const commit = () => {
    if (draft === null) return
    const iso = isBlankDate(draft) ? null : toIsoDate(draft)
    if (!isBlankDate(draft) && iso === null) {
      setShowError(true)
      return
    }
    setDraft(null)
    setShowError(false)
    const stored = isBlankDate(value) ? null : toIsoDate(String(value)) ?? value
    if (iso !== stored) onChange(iso)
  }

  return (
    <div
      className={`${BOX_BASE} justify-end bg-white ${invalid ? 'border border-brand-statusSale' : EDITABLE_BORDER}`}
      style={{ width }}
    >
      <input
        type="text"
        inputMode="text"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        ref={inputRef}
        value={shown}
        // Letter 99, 7.1.2: an empty date shows the expected shape (or the caller's own placeholder).
        placeholder={placeholder}
        onChange={(e) => {
          history.onEdit(e, shown)
          setDraft(e.target.value)
        }}
        onFocus={() => history.onFocus(shown)}
        // Undo restores text only; it is committed on blur or Enter like typing.
        onKeyDown={(e) => {
          if (history.onKeyDown(e)) return
          if (e.key === 'Enter') commit()
        }}
        // Same window/tab-switch guard as EditableValueBox: the input is still activeElement, so the
        // draft is kept and nothing is committed yet.
        onBlur={(e) => {
          if (document.activeElement === e.target) return
          commit()
        }}
        className={`min-w-0 flex-1 bg-transparent outline-none text-right text-[14px] font-normal text-brand-dark ${placeholderDark ? 'placeholder:text-brand-dark' : 'placeholder:text-brand-placeholder'}`}
      />
      {invalid ? (
        <span id={errorId} className="sr-only">
          Enter a valid date as M/D/YY
        </span>
      ) : null}
    </div>
  )
}
